import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { gql } from "graphql-tag";
import logger from "services/logger";
import { ServerError, LoadingSpinner } from "components/graphql-views";
import { getProperty, updatePropertyAdmin } from "types/gql-types";
import { useSnackbar } from "components/haven-design/snackbar";
import PropertyForm, { Property, formSchema } from "./form";

const PROPERTY = gql`
  query getProperty($id: ID!) {
    property(id: $id) {
      id
      organizationId
      name
      phone
      phoneExt
      email
      interestListId
      address {
        address1
        address2
        city
        state
        zip
      }
      submissionDigestEnabled
      units
      invoicedCustomer {
        invoicedId
        active
      }
    }
  }
`;

const UPDATE_PROPERTY = gql`
  mutation updatePropertyAdmin($id: ID!, $data: PropertyUpdateInput!) {
    updateProperty(id: $id, data: $data) {
      id
      name
      invoicedCustomer {
        invoicedId
        active
      }
    }
  }
`;

const UpdateProperty: React.FC<{ propertyId: string }> = ({ propertyId }) => {
  const { openSnackbar } = useSnackbar();
  const [sending, setSending] = useState(false);

  const { loading, error, data } = useQuery<getProperty>(PROPERTY, {
    variables: { id: propertyId },
    fetchPolicy: "cache-and-network",
  });

  const [updateProperty] = useMutation<updatePropertyAdmin>(UPDATE_PROPERTY);

  const submitForm = (formData: Property) => {
    setSending(true);

    updateProperty({ variables: { id: propertyId, data: formData } })
      .then(() => {
        openSnackbar({ message: "Saved", status: "success" });
        setSending(false);
      })
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({ message: "Could not save property", status: "error" });
        setSending(false);
      });
  };

  if (error) return <ServerError desc={error.message} />;
  if (loading) return <LoadingSpinner />;
  if (!data) return null;

  // casting strips __typename from the appollo response data
  // later this will also take null data and make it an empty string
  const property = formSchema.cast(data.property);

  return (
    <div>
      <PropertyForm
        formType="update"
        // @ts-ignore
        initialData={property}
        sending={sending}
        handleSubmit={submitForm}
      />
    </div>
  );
};

export default UpdateProperty;
